/**
 * Parses FDX-indented screenplay text to extract dialog lines.
 * Pure function — no store dependencies, unit-testable.
 *
 * FDX indentation rules (character counts from left margin):
 *   - Character name: indent >= 17, ALL CAPS
 *   - Parenthetical: indent 12–16, starts with (
 *   - Dialog: indent 7–14 after a character name
 *   - Action/scene heading: indent 0–6 (flushes current block)
 */

export interface DialogLine {
  character: string;
  text: string;
  parenthetical?: string;
  lineIndex: number;   // index of the character-name line in the original text
}

/** Strip suffixes like (V.O.), (O.S.), (CONT'D) from character names */
export function cleanCharacterName(raw: string): string {
  return raw
    .replace(/\s*\(V\.?O\.?\)\s*/gi, '')
    .replace(/\s*\(O\.?S\.?\)\s*/gi, '')
    .replace(/\s*\(CONT'?D\)\s*/gi, '')
    .trim();
}

/** Check if a string is ALL CAPS (ignoring non-alpha) */
function isAllCaps(s: string): boolean {
  const alpha = s.replace(/[^A-Za-z]/g, '');
  return alpha.length > 0 && alpha === alpha.toUpperCase();
}

/** Measure leading whitespace (spaces) */
function leadingSpaces(line: string): number {
  const match = line.match(/^( *)/);
  return match ? match[1].length : 0;
}

/**
 * Parse screenplay text and extract dialog lines.
 * Returns dialog lines in order of appearance.
 */
export function parseScreenplayDialog(sceneText: string): DialogLine[] {
  const lines = sceneText.split('\n');
  const result: DialogLine[] = [];

  let currentCharacter: string | null = null;
  let currentParenthetical: string | undefined;
  let currentDialog: string[] = [];
  let currentLineIndex = 0;

  function flushDialog(): void {
    if (currentCharacter && currentDialog.length > 0) {
      result.push({
        character: currentCharacter,
        text: currentDialog.join(' ').trim(),
        parenthetical: currentParenthetical,
        lineIndex: currentLineIndex,
      });
    }
    currentCharacter = null;
    currentParenthetical = undefined;
    currentDialog = [];
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trimEnd();
    if (trimmed.length === 0) continue;

    const indent = leadingSpaces(trimmed);
    const content = trimmed.trim();

    // Character name: indent >= 17, ALL CAPS
    if (indent >= 17 && isAllCaps(content)) {
      flushDialog();
      currentCharacter = cleanCharacterName(content);
      currentLineIndex = i;
      continue;
    }

    // Parenthetical: indent 12–16, starts with (
    if (indent >= 12 && indent <= 16 && content.startsWith('(') && currentCharacter) {
      currentParenthetical = content;
      continue;
    }

    // Dialog text: indent 7–14 after character name
    if (indent >= 7 && indent <= 14 && currentCharacter) {
      currentDialog.push(content);
      continue;
    }

    // Action / scene heading: indent 0–6 — flush current block
    if (indent <= 6) {
      flushDialog();
      continue;
    }

    // Anything else with a current character — treat as dialog continuation
    if (currentCharacter && indent > 6) {
      currentDialog.push(content);
    }
  }

  // Flush final block
  flushDialog();

  // If FDX parsing found nothing, try plain-text (Fountain-style) parsing
  if (result.length === 0) {
    return parsePlainTextDialog(sceneText);
  }

  return result;
}

/**
 * Strip common markup from pasted screenplay text:
 * - <center>NAME</center> → NAME
 * - > blockquote prefix → stripped
 * - Other HTML tags → stripped
 */
function stripMarkup(text: string): string {
  return text
    .replace(/<center>(.*?)<\/center>/gi, '$1')   // unwrap <center> tags
    .replace(/<\/?[a-z][^>]*>/gi, '')              // strip remaining HTML tags
    .replace(/^>\s?/gm, '');                       // strip > blockquote prefix
}

/**
 * Parse un-indented screenplay text (Fountain-style).
 * Heuristic: a short ALL-CAPS line followed by non-empty lines = character + dialog.
 * Blank lines separate blocks. Strips HTML/markdown markup first.
 */
function parsePlainTextDialog(sceneText: string): DialogLine[] {
  const cleaned = stripMarkup(sceneText);
  const lines = cleaned.split('\n');
  const result: DialogLine[] = [];

  let i = 0;
  while (i < lines.length) {
    const trimmed = lines[i].trim();

    // Skip empty lines
    if (!trimmed) { i++; continue; }

    // Check if this line looks like a character name:
    // ALL CAPS, ≤40 chars, no period at end (not a sentence)
    if (isAllCaps(trimmed) && trimmed.length <= 40 && !trimmed.endsWith('.')) {
      const character = cleanCharacterName(trimmed);
      const lineIndex = i;
      i++;

      // Check for optional parenthetical
      let parenthetical: string | undefined;
      if (i < lines.length) {
        const next = lines[i].trim();
        if (next.startsWith('(') && next.endsWith(')')) {
          parenthetical = next;
          i++;
        }
      }

      // Collect dialog lines until blank line or next character name
      const dialogParts: string[] = [];
      while (i < lines.length) {
        const dl = lines[i].trim();
        if (!dl) break; // blank line ends block
        // If this looks like a new character name, stop
        if (isAllCaps(dl) && dl.length <= 40 && !dl.endsWith('.')) break;
        dialogParts.push(dl);
        i++;
      }

      if (character && dialogParts.length > 0) {
        result.push({
          character,
          text: dialogParts.join(' '),
          parenthetical,
          lineIndex,
        });
      }
    } else {
      // Not a character name — skip (action line, scene heading, etc.)
      i++;
    }
  }

  return result;
}

/** Extract unique character names in first-appearance order */
export function extractCharacters(lines: DialogLine[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const line of lines) {
    if (!seen.has(line.character)) {
      seen.add(line.character);
      result.push(line.character);
    }
  }
  return result;
}
